import { Flex, Form, Select } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { useFilterOptions } from 'business/quality-control/pages/segment-history/hooks/use-filter-options';
import {
  SegmentRange,
  useSegmentHistoryFilters,
  useSegmentHistoryFiltersFormName,
} from 'business/quality-control/pages/segment-history/providers/filters';
import { Module_Enum, useGetFiltersDataQuery } from 'generated/graphql';
import { DateRangePicker } from 'ui/form/date-range-picker';
import { NumberRangeInputWithMessage } from 'ui/form/number-range-input-with-message';

import styles from './index.module.scss';

export const SegmentHistoryFilters = () => {
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site');

  const { t } = useTranslation();
  const [form] = Form.useForm();

  const formName = useSegmentHistoryFiltersFormName();
  const filters = useSegmentHistoryFilters();

  const graphqlHeaders = useGraphqlApiHeaders(Module_Enum.QualityScan);
  const { data, loading } = useGetFiltersDataQuery({
    variables: {
      constructionSiteId: currentConstructionSite.id,
      projectId: currentConstructionSite.projectId,
    },
    context: { headers: graphqlHeaders },
  });
  const {
    siteOptions,
    segmentRangeOptions,
    segmentTypeOptions,
    statusOptions,
    serialNumberOptions,
    segmentDateOptions,
    setFirstSerialNumberOption,
  } = useFilterOptions(data);

  return (
    <Form layout="inline" form={form} name={formName} initialValues={filters}>
      <Form.Item
        name="serialNumber"
        label={t('input.serialNumber.label')}
        layout="vertical"
      >
        <Select
          placeholder={t('input.serialNumber.placeholder')}
          showSearch
          allowClear
          onSearch={(value) => {
            setFirstSerialNumberOption(value);
          }}
          onClear={() => setFirstSerialNumberOption(null)}
          options={serialNumberOptions}
          loading={loading}
          className={styles.segmentDetailsSelect}
        />
      </Form.Item>

      <Form.Item
        name="segmentType"
        label={t('input.segmentTypes.selectLabel')}
        layout="vertical"
      >
        <Select
          placeholder={t('input.segmentTypes.placeholder')}
          options={segmentTypeOptions}
          className={styles.segmentDetailsSelect}
          allowClear
        />
      </Form.Item>

      <Form.Item
        name="site"
        label={t('input.scanSites.selectLabel')}
        layout="vertical"
      >
        <Select
          placeholder={t('input.scanSites.placeholder')}
          options={siteOptions}
          className={styles.segmentDetailsSelect}
          allowClear
        />
      </Form.Item>

      <Flex>
        <Form.Item
          name="segmentDate"
          label={t('input.segmentDate.label')}
          layout="vertical"
        >
          <Select
            placeholder={t('input.segmentDate.placeholder')}
            allowClear
            options={segmentDateOptions}
            className={styles.ringRangeSelect}
          />
        </Form.Item>
        <Form.Item name="segmentDateRange" label={' '} layout="vertical">
          <DateRangePicker allowClear format="L" />
        </Form.Item>
      </Flex>

      <Form.Item
        name="status"
        label={t('input.status.label')}
        layout="vertical"
      >
        <Select
          placeholder={t('input.status.placeholder')}
          mode="multiple"
          allowClear
          options={statusOptions}
          className={styles.segmentDetailsSelect}
        />
      </Form.Item>

      <Form.Item
        name="segmentRange"
        label={t('input.segmentRange.selectLabel')}
        layout="vertical"
      >
        <Select
          placeholder={t('input.segmentRange.placeholder')}
          options={segmentRangeOptions}
          className={styles.ringRangeSelect}
          allowClear
        />
      </Form.Item>

      {filters.segmentRange === SegmentRange.interval ? (
        <NumberRangeInputWithMessage
          name="ringNumberRange"
          label={t('input.segmentRange.rangeLabel')}
          leftPlaceholder={t('input.ringRange.startPlaceholder')}
          rightPlaceholder={t('input.ringRange.endPlaceholder')}
        />
      ) : null}
    </Form>
  );
};
